import {
	Avatar,
	FloatingLoader,
	SearchInput,
	Skeleton,
	Typography,
} from "@/components";
import { BackAppBar } from "@/components/ui";
import useChannel from "@/hooks/useChannel";
import useContactPeoples from "@/hooks/useContactPeoples";
import { useDebounce } from "@/hooks/useDebounce";
import { genChannelName } from "@/libraries/helper";
import AddIcon from "@mui/icons-material/Add";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { toast } from "sonner";

export const Route = createFileRoute("/dashboard/chat/single/create")({
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = Route.useNavigate();
	const [viewRef, inView] = useInView();
	const [search, setSearch] = useState("");
	const debouncedSearch = useDebounce(search, 500);
	const contacts = useContactPeoples({
		isDirectory: false,
		search: debouncedSearch,
		excludeProfile: true,
		estimateSize: 50,
	});

	const { mutateCreateChannel } = useChannel();

	const onClickList = async (people: (typeof contacts.peoples)[number]) => {
		try {
			let channelName = people.channel_name;
			if (!channelName) {
				const resNwChannel = await mutateCreateChannel.mutateAsync({
					name: genChannelName("single"),
					messageable_id: people.id.toString(),
					messageable_type: "User",
				});
				channelName = String(resNwChannel.user.channel_name);
			}
			return navigate({
				to: "/dashboard/chat/single/$id/$channelName",
				params: { id: people.id, channelName },
			});
		} catch (err) {
			return toast.error("Failed to start chat, please try again");
		}
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (inView) {
			contacts.fetchNextPage();
		}
	}, [
		contacts.fetchNextPage,
		contacts.hasNextPage,
		contacts.isFetchingNextPage,
		inView,
	]);

	return (
		<div className="relative bg-white min-h-full">
			<BackAppBar title="Select Contact">
				<div className="container space-y-4">
					<SearchInput
						name="search"
						label="Search"
						placeholder="Search"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
					/>
				</div>
			</BackAppBar>
			<FloatingLoader open={contacts.isLoading} />
			<div className="relative h-full my-4">
				<div className="container">
					<Typography fontWeight={700} fontSize={14}>
						Groups
					</Typography>
				</div>
				<List disablePadding>
					<ListItem>
						<ListItemButton
							dense
							sx={{ px: 1 }}
							onClick={() =>
								navigate({
									to: "/dashboard/chat/group/create",
								})
							}
						>
							<ListItemAvatar>
								<Avatar className="!bg-primary-light !text-primary">
									<AddIcon />
								</Avatar>
							</ListItemAvatar>
							<ListItemText primary="CREATE GROUP" className="text-primary" />
						</ListItemButton>
					</ListItem>
				</List>
				<div className="container">
					<Typography fontWeight={700} fontSize={14}>
						People
					</Typography>
				</div>
				<List disablePadding>
					{contacts.peoples.map((people) => (
						<ListItem key={people.id} onClick={() => onClickList(people)}>
							<ListItemButton dense sx={{ px: 1 }}>
								<ListItemAvatar>
									<Avatar
										alt={people.first_name || "L"}
										className="!bg-gray-light !text-dark"
										src={people.avatar ?? "/broken-image.jpg"}
										status={people.status}
									/>
								</ListItemAvatar>
								<ListItemText primary={people.fullname} />
							</ListItemButton>
						</ListItem>
					))}
					{!contacts.isFetchingNextPage && contacts.hasNextPage ? (
						<Skeleton ref={viewRef} className="h-12 w-full" />
					) : null}
				</List>
			</div>
		</div>
	);
}
